import React, { useState } from "react";
import { resetPassword } from "../../api";
import { handleApiError } from "../../utils/apiErrorHandler";
import useGoToPricing from "../../hooks/useGoToPricing";
import Header from "../Homepage/sections/Header";
import { Page, HomepageGlobalStyles } from "../Homepage/styles";
import {
  LoginAlert,
  LoginCard,
  LoginCardHeader,
  LoginField,
  LoginFooterLink,
  LoginFooterNote,
  LoginForm,
  LoginGlow,
  LoginHomeLink,
  LoginHomeWrap,
  LoginInner,
  LoginInput,
  LoginInputIcon,
  LoginInputWrap,
  LoginLabel,
  LoginMain,
  LoginPage as LoginPageRoot,
  LoginShell,
  LoginSpinIcon,
  LoginSubmit,
  LoginSubtitle,
  LoginSuccessAlert,
  LoginTitle,
} from "../Login/Login.styles";

const MailIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="20" height="16" x="2" y="4" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const AlertCircleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" x2="12" y1="8" y2="12" />
    <line x1="12" x2="12.01" y1="16" y2="16" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const LoaderIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </svg>
);

const SUCCESS_MESSAGE =
  "We've sent a password reset link. Please check your email.";

/**
 * Lovable Forgot shell (same as Login) + prod resetPassword API.
 */
const Forgot = () => {
  const goToPricing = useGoToPricing();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess(false);

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setError("Please enter your email address.");
      return;
    }

    setIsSubmitting(true);
    const res = await resetPassword(trimmedEmail);

    if (res && res.status >= 200 && res.status < 300) {
      setSuccess(true);
      setIsSubmitting(false);
      return;
    }

    const errorResponse = handleApiError(res, "Failed to reset password", {
      notify: false,
    });
    setError(errorResponse.message);
    setIsSubmitting(false);
  };

  return (
    <Page>
      <HomepageGlobalStyles />
      <LoginPageRoot>
        <LoginGlow aria-hidden="true" />
        <LoginShell>
          <Header onSubscribeClick={goToPricing} hideSpacer />

          <LoginMain>
            <LoginInner>
              <LoginCard>
                <LoginCardHeader>
                  <LoginTitle>Reset your password</LoginTitle>
                  <LoginSubtitle>
                    Enter your email and we&apos;ll send you a link to choose a
                    new password.
                  </LoginSubtitle>
                </LoginCardHeader>

                <LoginForm onSubmit={onSubmit} noValidate>
                  {error ? (
                    <LoginAlert role="alert">
                      <AlertCircleIcon />
                      <span>{error}</span>
                    </LoginAlert>
                  ) : null}

                  {success ? (
                    <LoginSuccessAlert role="status">
                      <CheckCircleIcon />
                      <span>{SUCCESS_MESSAGE}</span>
                    </LoginSuccessAlert>
                  ) : null}

                  <LoginField>
                    <LoginLabel htmlFor="forgot-email">Email</LoginLabel>
                    <LoginInputWrap>
                      <LoginInputIcon>
                        <MailIcon />
                      </LoginInputIcon>
                      <LoginInput
                        id="forgot-email"
                        type="email"
                        autoComplete="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={isSubmitting}
                      />
                    </LoginInputWrap>
                  </LoginField>

                  <LoginSubmit
                    type="submit"
                    disabled={isSubmitting || !email.trim()}
                  >
                    {isSubmitting ? (
                      <>
                        <LoginSpinIcon>
                          <LoaderIcon />
                        </LoginSpinIcon>
                        Sending…
                      </>
                    ) : (
                      "Send reset link"
                    )}
                  </LoginSubmit>
                </LoginForm>
              </LoginCard>

              <LoginFooterNote>
                Remember your password?{" "}
                <LoginFooterLink to="/login">Back to login</LoginFooterLink>
              </LoginFooterNote>

              <LoginHomeWrap>
                <LoginHomeLink to="/">
                  <ArrowLeftIcon />
                  Back to home
                </LoginHomeLink>
              </LoginHomeWrap>
            </LoginInner>
          </LoginMain>
        </LoginShell>
      </LoginPageRoot>
    </Page>
  );
};

export default Forgot;
